import api from '../client';

/**
 * Service pour la gestion des produits (e-commerce)
 */
const productService = {
  /**
   * Récupérer la liste des produits
   * @param {Object} params - Paramètres (category, inStock, page, limit, minPrice, maxPrice)
   * @returns {Promise} Liste des produits avec pagination
   */
  getProducts: async (params = {}) => {
    try {
      const response = await api.get('/products', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer un produit par son ID
   * @param {string} id - ID du produit
   * @returns {Promise} Données du produit
   */
  getProductById: async (id) => {
    try {
      const response = await api.get(`/products/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Rechercher des produits
   * @param {Object} params - Paramètres de recherche (q, minPrice, maxPrice, category)
   * @returns {Promise} Résultats de recherche
   */
  searchProducts: async (params) => {
    try {
      const response = await api.get('/products/search', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Créer un nouveau produit
   * @param {Object} productData - Données du produit
   * @returns {Promise} Produit créé
   */
  createProduct: async (productData) => {
    try {
      const response = await api.post('/products', productData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Mettre à jour un produit
   * @param {string} id - ID du produit
   * @param {Object} productData - Nouvelles données
   * @returns {Promise} Produit mis à jour
   */
  updateProduct: async (id, productData) => {
    try {
      const response = await api.put(`/products/${id}`, productData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Supprimer un produit
   * @param {string} id - ID du produit
   * @returns {Promise} Confirmation de suppression
   */
  deleteProduct: async (id) => {
    try {
      const response = await api.delete(`/products/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Mettre à jour le stock d'un produit
   * @param {string} id - ID du produit
   * @param {number} quantity - Nouvelle quantité
   * @returns {Promise} Produit mis à jour
   */
  updateStock: async (id, quantity) => {
    try {
      const response = await api.patch(`/products/${id}/stock`, { quantity });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer les catégories de produits
   * @returns {Promise} Liste des catégories
   */
  getCategories: async () => {
    try {
      const response = await api.get('/products/categories');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  uploadImages: async (files) => {
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('images', file));
      const response = await api.post('/products/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Parse un fichier Excel ou PDF via l'IA et retourne une prévisualisation.
  // Ne crée aucun produit. onProgress(percent) est optionnel pour la barre d'upload.
  previewImport: async (file, onProgress) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/products/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 min — l'IA peut prendre du temps sur de gros fichiers
        onUploadProgress: (e) => {
          if (onProgress && e.total) {
            onProgress(Math.round((e.loaded * 100) / e.total));
          }
        },
      });
      return response.data;
    } catch (error) {
      const data = error.response?.data || error;
      // On attache le statut HTTP pour que les appelants puissent décider de retenter.
      if (typeof data === 'object' && data !== null) {
        data._httpStatus = error.response?.status;
      }
      throw data;
    }
  },

  // Crée les produits prévisualisés après confirmation de l'utilisateur.
  confirmImport: async (products) => {
    try {
      const response = await api.post('/products/import/confirm', { products }, {
        timeout: 120000,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // ── Coupons digitaux ──────────────────────────────────────────────────────

  getProductCoupons: async (productId) => {
    try {
      const response = await api.get(`/products/${productId}/coupons`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  addProductCoupons: async (productId, codes) => {
    try {
      const response = await api.post(`/products/${productId}/coupons`, { codes });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  deleteProductCoupon: async (productId, couponId) => {
    try {
      const response = await api.delete(`/products/${productId}/coupons/${couponId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

export default productService;
